import { useEffect, useMemo, useState } from 'react';

import NavigationTabs, { TabProps } from '@apigrowfinance/pbolt-navigation-tabs';
import { usePermission } from '@apigrowfinance/pbolt-permission-provider';
import { useShortcuts } from '@apigrowfinance/pbolt-shortcut-provider';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAccount } from '@contexts/AccountProvider';
import { ProgramParameters } from '@enums/programs/programParameters';

const AccountTabs = () => {
    const { account, programHasParameter, isLoading } = useAccount();
    const { formatMessage } = useIntl();
    const [selectedTab, setSelectedTab] = useState('overview');
    const navigate = useNavigate();
    const location = useLocation();
    const { isAllowedTo } = usePermission();
    const { addShortcut, removeShortcut } = useShortcuts();

    const creditCardTabs = useMemo(() => {
        const tabArray = [
            {
                label: formatMessage({
                    id: `AccountTabs.Transactions`,
                    defaultMessage: 'Transactions',
                }),
                type: 'transactions',
                handler: () => navigate(`/${account?.account_id}/transactions`),
            },
            {
                label: formatMessage({
                    id: `AccountTabs.Statements`,
                    defaultMessage: 'Statements',
                }),
                type: 'statements',
                handler: () => navigate(`/${account?.account_id}/statements`),
            },
            {
                label: formatMessage({
                    id: `AccountTabs.Limits`,
                    defaultMessage: 'Limits',
                }),
                type: 'limits',
                handler: () =>
                    navigate(
                        `/${account?.account_id}/customers/${account?.customer_id}/limits`,
                    ),
            },
            ...(programHasParameter(ProgramParameters.DELINQUENCY)
                ? [
                      {
                          label: formatMessage({
                              id: `AccountTabs.delinquency`,
                              defaultMessage: 'Delinquency',
                          }),
                          type: 'delinquency',
                          handler: () =>
                              navigate(`/${account?.account_id}/delinquency`),
                      },
                  ]
                : []),
        ];
        // If we can view cards add the cards tab
        if (isAllowedTo('view.cardsList')) {
            tabArray.splice(0, 0, {
                label: formatMessage({
                    id: `AccountTabs.Cards`,
                    defaultMessage: 'Cards',
                }),
                type: 'cards',
                handler: () => navigate(`/${account?.account_id}/cards`),
            });
        }
        return tabArray;
    }, [account, formatMessage, isAllowedTo, navigate, programHasParameter]);

    const debitCardTabs = useMemo(() => {
        const tabArray = [
            {
                label: formatMessage({
                    id: `AccountTabs.Transactions`,
                    defaultMessage: 'Transactions',
                }),
                type: 'transactions',
                handler: () => navigate(`/${account?.account_id}/transactions`),
            },
            {
                label: formatMessage({
                    id: `AccountTabs.Limits`,
                    defaultMessage: 'Limits',
                }),
                type: 'limits',
                handler: () =>
                    navigate(
                        `/${account?.account_id}/customers/${account?.customer_id}/limits`,
                    ),
            },
        ];
        // If we can view cards add the cards tab
        if (isAllowedTo('view.cardsList')) {
            tabArray.splice(0, 0, {
                label: formatMessage({
                    id: `AccountTabs.Cards`,
                    defaultMessage: 'Cards',
                }),
                type: 'cards',
                handler: () =>
                    navigate(
                        `/${account?.account_id}/customers/${account?.customer_id}/cards`,
                    ),
            });
        }
        return tabArray;
    }, [account, formatMessage, isAllowedTo, navigate]);

    const lendingTabs = useMemo(() => {
        return [
            {
                label: formatMessage({
                    id: `AccountTabs.loan`,
                    defaultMessage: 'Loan',
                }),
                type: 'loan',
                handler: () =>
                    navigate(
                        `/${account?.account_id}/lending/${account?.account_id}/loan`,
                    ),
            },
            {
                label: formatMessage({
                    id: `AccountTabs.payments`,
                    defaultMessage: 'Payments',
                }),
                type: 'payments',
                handler: () =>
                    navigate(
                        `/${account?.account_id}/lending/${account?.account_id}/payments`,
                    ),
            },
            {
                label: formatMessage({
                    id: `AccountTabs.disbursements`,
                    defaultMessage: 'Disbursements',
                }),
                type: 'disbursements',
                handler: () =>
                    navigate(
                        `/${account?.account_id}/lending/${account?.account_id}/disbursements`,
                    ),
            },
            // {
            //     label: formatMessage({
            //         id: `AccountTabs.loan`,
            //         defaultMessage: 'Loan',
            //     }),
            //     type: 'loan',
            //     handler: () => {
            //         return navigate(`/${account?.account_id}/loan`);
            //     },
            // },
            // {
            //     label: formatMessage({
            //         id: `AccountTabs.payments`,
            //         defaultMessage: 'Payments',
            //     }),
            //     type: 'payments',
            //     handler: () => navigate(`/${account?.account_id}/payments`),
            // },
            // {
            //     label: formatMessage({
            //         id: `AccountTabs.disbursements`,
            //         defaultMessage: 'Disbursements',
            //     }),
            //     type: 'disbursements',
            //     handler: () =>
            //         navigate(`/${account?.account_id}/disbursements`),
            // },
        ];
    }, [account, formatMessage, navigate]);

    const tabsToDisplay = useMemo(() => {
        switch (account?.program_type) {
            case 'LENDING':
                return lendingTabs;
            case 'CREDITO':
                return creditCardTabs;
            case 'DEBITO':
            default:
                return debitCardTabs;
        }
    }, [account?.program_type, creditCardTabs, debitCardTabs, lendingTabs]);

    const tabs: TabProps[] = useMemo(() => {
        return [
            {
                label: formatMessage({
                    id: `AccountTabs.Overview`,
                    defaultMessage: 'Overview',
                }),
                type: 'overview',
                handler: () => navigate(`/${account?.account_id}/overview`),
            },
            ...tabsToDisplay,
            {
                label: formatMessage({
                    id: `AccountTabs.Timeline`,
                    defaultMessage: 'Timeline',
                }),
                type: 'timeline',
                handler: () => navigate(`/${account?.account_id}/timeline`),
            },
            {
                label: formatMessage({
                    id: `AccountTabs.History`,
                    defaultMessage: 'History',
                }),
                type: 'history',
                handler: () =>
                    navigate(
                        `/${account?.account_id}/customers/${account?.customer_id}/history`,
                    ),
            },
        ];
    }, [account, formatMessage, navigate, tabsToDisplay]);

    // Add shortcuts to tabs
    useEffect(() => {
        tabs.map((tab) => {
            addShortcut({
                alias: tab.type.toUpperCase(),
                combination: `shift+${
                    tabs.findIndex((t) => t.type === tab.type) + 1
                }`,
                handler: tab.handler,
            });
        });

        // Remove shortcuts on unmount
        return () => {
            tabs.map((tab) => {
                removeShortcut(tab.type.toUpperCase());
            });
        };
    }, [addShortcut, removeShortcut, tabs]);

    // checking url to work out which tab we're on
    useEffect(() => {
        tabs.map((tab) => {
            const matchPath = location.pathname.includes(tab.type);
            if (matchPath) {
                setSelectedTab(tab.type);
            }
        });
    }, [location.pathname, tabs]);

    return (
        <NavigationTabs
            tabs={tabs}
            selectedTab={selectedTab}
            isLoading={isLoading}
        />
    );
};

export default AccountTabs;
